<style scoped>
  .context-file-row {
    display: flex;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #e7eaec;
  }

  .context-file-row:last-of-type {
    border-bottom: none;
  }

  .context-file-name {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .context-file-meta {
    color: #999;
    font-size: 12px;
    margin-right: 12px;
    white-space: nowrap;
  }
</style>

<template>
  <fragment>
    <h2 class="card-title">{{ title }}</h2>
    <div class="card">
      <div class="card-body">
        <p class="text-muted small mb-3">
          Documents, audio recordings and images related to this {{ noun }}
          (meeting notes, requirements, whiteboards). The AI assistants use them
          as background and they can be downloaded here. They are internal &mdash;
          not visible to the client.
        </p>

        <div class="context-file-row" v-for="file in files" :key="file.id">
          <a class="context-file-name" href="#" title="Download this file" @click.prevent="download(file)">
            <i class="fal fa-fw" :class="fileIcon(file.kind)"></i>
            {{ file.name }}
          </a>
          <span class="context-file-meta">{{ file.created_at | moment("DD/MM/YYYY") }}</span>
          <button
            type="button"
            class="btn btn-sm btn-link text-danger"
            :disabled="removing === file.id"
            @click="remove(file)"
            title="Remove this file from the AI context"
          >
            <i class="fal" :class="removing === file.id ? 'fa-spinner fa-pulse' : 'fa-trash-alt'"></i>
          </button>
        </div>

        <p class="text-muted" v-if="!files.length && !loading">No context files uploaded yet.</p>

        <div class="mt-3">
          <button type="button" class="btn btn-sm btn-primary" :disabled="uploading" @click="$refs.fileInput.click()">
            <i class="fal" :class="uploading ? 'fa-spinner fa-pulse' : 'fa-paperclip'"></i>
            Upload Context File
          </button>
          <input
            type="file"
            ref="fileInput"
            style="display: none"
            accept=".pdf,audio/*,image/*"
            @change="upload"
          />
        </div>
      </div>
    </div>
  </fragment>
</template>

<script>
import { http, notify, apiUrl } from 'utils'
import { fileIcon } from 'components/ai/chat'

export default {
  props: {
    id: {
      required: true,
      type: [Number, String],
    },
    // The API resource type the files hang off: 'opportunities' or 'projects'.
    type: {
      type: String,
      default: 'opportunities',
    },
    title: {
      type: String,
      default: 'Files',
    },
  },

  data: function () {
    return {
      files: [],
      loading: true,
      uploading: false,
      removing: null,
    }
  },

  computed: {
    noun() {
      return this.type === 'projects' ? 'project' : 'opportunity'
    },

    endpoint() {
      return `ai/${this.type}/${this.id}/context-files`
    },
  },

  created: function () {
    this.load()
  },

  methods: {
    fileIcon,

    download(file) {
      window.location.href = apiUrl(`files/${file.id}`)
    },

    async load() {
      try {
        const { data } = await http.get(this.endpoint)
        this.files = data.data
      } catch (error) {
        this.files = []
      } finally {
        this.loading = false
      }
    },

    async upload(event) {
      const file = event.target.files[0]
      event.target.value = null

      if (!file) return

      const form = new FormData()
      form.append('file', file)

      this.uploading = true

      try {
        const { data } = await http.post(
          this.endpoint,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )

        this.files.push(data.data)
        notify.success(`${file.name} added as AI context for this ${this.noun}.`)
      } catch (error) {
        if (error.response && error.response.status === 422) {
          notify.error('That file type is not supported. Upload a PDF, audio file or image.')
        } else {
          notify.error('Failed to upload the context file, please try again.')
        }
      } finally {
        this.uploading = false
      }
    },

    async remove(file) {
      this.removing = file.id

      try {
        await http.delete(`${this.endpoint}/${file.id}`)

        this.files = this.files.filter((f) => f.id !== file.id)
      } catch (error) {
        notify.error('Failed to remove the context file, please try again.')
      } finally {
        this.removing = null
      }
    },
  },
}
</script>
